import { useState, useMemo, useEffect } from 'react';
import { useDefiProtocols, DefiProtocol, PFQuality } from '@/hooks/useDefiProtocols';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { CategoryBadge } from '@/components/CategoryBadge';
import { formatUSD, formatPercent, getChangeColor, safeToFixed } from '@/lib/format';
import { Layers, Search, AlertCircle, ChevronUp, ChevronDown, Settings2, TrendingUp, CheckCircle, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PFRatioHistoryChart } from '@/components/PFRatioHistoryChart';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Helper to get quality icon and color
const getQualityIndicator = (quality: PFQuality | undefined) => {
  switch (quality) {
    case 'good':
      return { icon: CheckCircle, color: 'text-green-500', tooltip: 'Dados confiáveis' };
    case 'high_but_valid':
      return { icon: AlertTriangle, color: 'text-yellow-500', tooltip: 'P/F alto mas válido' };
    case 'suspicious_low':
      return { icon: AlertCircle, color: 'text-red-500', tooltip: 'Suspeito: P/F muito baixo' };
    case 'suspicious_high':
      return { icon: AlertCircle, color: 'text-red-500', tooltip: 'Suspeito: P/F muito alto' };
    default:
      return null;
  }
};
// Column definitions
interface ColumnDef {
  key: string;
  label: string;
  shortLabel?: string;
  defaultVisible: boolean;
  align?: 'left' | 'right';
  format?: (value: any) => string;
  colorFn?: (value: any) => string;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'tvl_usd', label: 'TVL', defaultVisible: true, align: 'right', format: formatUSD },
  { key: 'tvl_change_24h', label: 'Δ TVL 24h', shortLabel: 'Δ24h', defaultVisible: false, align: 'right', format: formatPercent, colorFn: getChangeColor },
  { key: 'tvl_change_7d', label: 'Δ TVL 7d', shortLabel: 'Δ7d', defaultVisible: false, align: 'right', format: formatPercent, colorFn: getChangeColor },
  { key: 'tvl_change_30d', label: 'Δ TVL 30d', shortLabel: 'Δ30d', defaultVisible: false, align: 'right', format: formatPercent, colorFn: getChangeColor },
  { key: 'fees_24h_usd', label: 'Fees 24h', defaultVisible: true, align: 'right', format: formatUSD },
  { key: 'fees_7d_usd', label: 'Fees 7d', defaultVisible: true, align: 'right', format: formatUSD },
  { key: 'fees_30d_usd', label: 'Fees 30d', defaultVisible: false, align: 'right', format: formatUSD },
  { key: 'fees_change_1d', label: 'Δ Fees 24h', shortLabel: 'Δ24h', defaultVisible: true, align: 'right', format: formatPercent, colorFn: getChangeColor },
  { key: 'fees_change_7d', label: 'Δ Fees 7d', shortLabel: 'Δ7d', defaultVisible: false, align: 'right', format: formatPercent, colorFn: getChangeColor },
  { key: 'fees_change_30d', label: 'Δ Fees 30d', shortLabel: 'Δ30d', defaultVisible: true, align: 'right', format: formatPercent, colorFn: getChangeColor },
  { key: 'price_to_fees_ann', label: 'P/F 30d', shortLabel: 'P/F 30d', defaultVisible: true, align: 'right', format: (v) => v != null ? `${safeToFixed(v, 1)}x` : '—' },
  { key: 'price_to_fees_ann_7d', label: 'P/F 7d', shortLabel: 'P/F 7d', defaultVisible: true, align: 'right', format: (v) => v != null ? `${safeToFixed(v, 1)}x` : '—' },
  { key: 'mcap_usd', label: 'Market Cap', defaultVisible: true, align: 'right', format: formatUSD },
];

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'Todas categorias' },
  { value: 'lending', label: 'Lending' },
  { value: 'dex', label: 'DEX' },
  { value: 'derivatives', label: 'Derivatives' },
  { value: 'staking', label: 'Staking' },
  { value: 'liquid staking', label: 'Liquid Staking' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'cdp', label: 'CDP' },
  { value: 'yield', label: 'Yield' },
];

const STORAGE_KEY = 'defi-protocols-columns';
const STORAGE_VERSION = 'v4'; // Bump to reset defaults when columns change (added Δ Fees 30d + Market Cap)

const DeFiProtocols = () => {
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('tvl_usd');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedProtocol, setSelectedProtocol] = useState<DefiProtocol | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const savedVersion = localStorage.getItem(STORAGE_KEY + '-version');
    const saved = localStorage.getItem(STORAGE_KEY);
    
    // If version changed or doesn't exist, use defaults
    if (savedVersion !== STORAGE_VERSION || !saved) {
      localStorage.setItem(STORAGE_KEY + '-version', STORAGE_VERSION);
      return ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);
    }
    
    try {
      return JSON.parse(saved);
    } catch {
      return ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);
    }
  });

  // Persist column preferences
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const { data, isLoading, error } = useDefiProtocols({ category, search });

  // Filter and sort protocols client-side
  const sortedProtocols = useMemo(() => {
    if (!data?.protocols) return [];
    
    let filtered = data.protocols;
    
    // Client-side search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        p.llama_slug.toLowerCase().includes(searchLower)
      );
    }

    // Sort
    return [...filtered].sort((a, b) => {
      const aVal = (a as any)[sortColumn] ?? (sortDirection === 'desc' ? -Infinity : Infinity);
      const bVal = (b as any)[sortColumn] ?? (sortDirection === 'desc' ? -Infinity : Infinity);
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [data?.protocols, search, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const visibleColumnDefs = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="glass-card">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Falha ao carregar protocolos DeFi. Tente novamente mais tarde.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="space-y-3 fade-in-up">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-4xl font-bold tracking-tight text-gradient">
              DeFi Protocols
            </h1>
            <Badge className="bg-primary/10 text-primary border-primary/20">
              <Layers className="h-3 w-3 mr-1" />
              {data?.total || 0} Protocolos
            </Badge>
          </div>
          <p className="text-muted-foreground text-lg max-w-3xl">
            TVL e Fees dos principais protocolos DeFi. Dados atualizados via DefiLlama.
          </p>
          <div className="gradient-line w-24" />
        </div>

        {/* Filters */}
        <Card className="glass-card p-4 fade-in-up delay-100">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar protocolo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
              />
            </div>
            
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] bg-background/50">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Column Selector */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="bg-background/50">
                  <Settings2 className="h-4 w-4 mr-2" />
                  Colunas
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground px-2 py-1">Selecione as colunas visíveis</p>
                  {ALL_COLUMNS.map(col => (
                    <div 
                      key={col.key}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleColumn(col.key)}
                    >
                      <Checkbox 
                        checked={visibleColumns.includes(col.key)}
                        onCheckedChange={() => toggleColumn(col.key)}
                      />
                      <span className="text-sm">{col.label}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </Card>

        {/* Table */}
        <Card className="glass-card overflow-hidden fade-in-up delay-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-muted-foreground font-medium">Protocolo</TableHead>
                  <TableHead className="text-muted-foreground font-medium">Categoria</TableHead>
                  {visibleColumnDefs.map(col => (
                    <TableHead 
                      key={col.key}
                      className={`text-muted-foreground font-medium cursor-pointer hover:text-foreground transition-colors select-none ${col.align === 'right' ? 'text-right' : ''}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                        <span>{col.shortLabel || col.label}</span>
                        {sortColumn === col.key && (
                          sortDirection === 'desc' 
                            ? <ChevronDown className="h-3 w-3" />
                            : <ChevronUp className="h-3 w-3" />
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i} className="border-border/30">
                      <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      {visibleColumnDefs.map(col => (
                        <TableCell key={col.key} className="text-right">
                          <Skeleton className="h-5 w-16 ml-auto" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortedProtocols.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2 + visibleColumnDefs.length} className="text-center py-8 text-muted-foreground">
                      Nenhum protocolo encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedProtocols.map((protocol) => (
                    <ProtocolRow 
                      key={protocol.llama_slug} 
                      protocol={protocol} 
                      columns={visibleColumnDefs}
                      onClick={() => setSelectedProtocol(protocol)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Last updated */}
        {data?.updated_at && (
          <p className="text-xs text-muted-foreground text-center fade-in-up delay-300">
            Última atualização: {new Date(data.updated_at).toLocaleString('pt-BR')}
          </p>
        )}
      </div>

      {/* Protocol Detail Sheet */}
      <Sheet open={!!selectedProtocol} onOpenChange={(open) => !open && setSelectedProtocol(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedProtocol && (
            <>
              <SheetHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-xl">{selectedProtocol.name}</SheetTitle>
                  <CategoryBadge category={selectedProtocol.category} />
                </div>
              </SheetHeader>
              
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">TVL</p>
                    <p className="text-lg font-mono font-semibold">
                      {formatUSD(selectedProtocol.tvl_usd)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Market Cap</p>
                    <p className="text-lg font-mono font-semibold">
                      {formatUSD(selectedProtocol.mcap_usd)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Fees 30d</p>
                    <p className="text-lg font-mono font-semibold">
                      {formatUSD(selectedProtocol.fees_30d_usd)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">P/F Ratio</p>
                    <p className="text-lg font-mono font-semibold">
                      {selectedProtocol.price_to_fees_ann 
                        ? `${safeToFixed(selectedProtocol.price_to_fees_ann, 1)}x` 
                        : '—'}
                    </p>
                  </div>
                </div>

                {/* P/F Historical Chart */}
                <PFRatioHistoryChart
                  slug={selectedProtocol.llama_slug}
                  geckoId={selectedProtocol.gecko_id}
                  protocolName={selectedProtocol.name}
                />

                {/* Additional info */}
                <div className="text-xs text-muted-foreground pt-4 border-t border-border">
                  <p>Slug: {selectedProtocol.llama_slug}</p>
                  {selectedProtocol.gecko_id && <p>CoinGecko ID: {selectedProtocol.gecko_id}</p>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Separate row component for performance
const ProtocolRow = ({ 
  protocol, 
  columns, 
  onClick 
}: { 
  protocol: DefiProtocol; 
  columns: ColumnDef[];
  onClick: () => void;
}) => {
  return (
    <TableRow 
      className="border-border/30 hover:bg-muted/10 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {protocol.name}
          {protocol.gecko_id && (
            <TrendingUp className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
      </TableCell>
      <TableCell>
        <CategoryBadge category={protocol.category} size="sm" />
      </TableCell>
      {columns.map(col => {
        const value = (protocol as any)[col.key];
        const formatted = col.format ? col.format(value) : String(value ?? '—');
        const colorClass = col.colorFn ? col.colorFn(value) : '';

        const isFees30dColumn = col.key === 'fees_change_30d';
        const fees30dSource = (protocol as any).fees_change_30d_source as string | undefined;
        const baseSlug = (protocol as any).base_slug as string | undefined;
        const isFees30dInherited = isFees30dColumn && value != null && fees30dSource === 'baseSlug' && !!baseSlug;
        
        // Add quality indicator for P/F columns
        const isPFColumn = col.key === 'price_to_fees_ann' || col.key === 'price_to_fees_ann_7d';
        const qualityKey = col.key === 'price_to_fees_ann' ? 'pf_quality_30d' : 'pf_quality_7d';
        const quality = isPFColumn ? (protocol as any)[qualityKey] as PFQuality : undefined;
        const qualityIndicator = quality ? getQualityIndicator(quality) : null;
        
        return (
          <TableCell 
            key={col.key} 
            className={`font-mono text-sm ${col.align === 'right' ? 'text-right' : ''} ${colorClass}`}
          >
            {isPFColumn && value != null && qualityIndicator ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-end gap-1">
                      <span>{formatted}</span>
                      <qualityIndicator.icon className={`h-3 w-3 ${qualityIndicator.color}`} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-xs">{qualityIndicator.tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : isFees30dInherited ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-end gap-1">
                      <span>{formatted}</span>
                      <Layers className="h-3 w-3 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p className="text-xs">Δ30d herdado de {baseSlug}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              formatted
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default DeFiProtocols;
